import React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  Grid,
  Typography,
  Tooltip,
  Box
} from "@mui/material";
import InfoIcon from '@mui/icons-material/Info';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { SalgVarerData } from "../../../models/NV01SALG";

interface VarerDetailsProps {
  varerdata: SalgVarerData[];
  sistesalg: string;
}
const merknadMap: Record<string, string> = {
  '1': 'Utgår – Kjøpeforslag blir ikke beregnet (Varen er utgått)',
  '2': 'På best. – Ikke beregnet når varen ikke skal føres',
  'M': 'Mid-utg. – Utsolgt fra leverandør (Opplysning)',
  '3': 'Nedskr. – Lagerverdi er nedskrevet',
  '4': 'Alt.art. – Alternativ vare (brukes ikke)',
  '5': 'Ikke pr. – Skal ikke fremkomme i prislister',
  '6': 'Må best – Må bestilles',
  'A': 'Prisavik – Skaffevarer, ikke på aviksliste',
  'B': 'Ikke bonus – Ikke bonus-beregning på denne vare',
  'F': 'Ekskludert – Ikke med i faktura/bonus/gebyr',
  'T': 'Tilb.pr. – Vare med tilbudspris',
  'X': 'Utgår – Kampanjevare (samme som kode 1)',
  'Y': 'Ikke lag. – Ikke på lagerstyring',
  'Z': 'Utgår – Kommisjon (samme som kode 1)',
  'O': 'ORIGINAL DEL',
};

const VarerDetails: React.FC<VarerDetailsProps> = ({ varerdata, sistesalg }) => {
  const sisteSalgDate = sistesalg && !isNaN(new Date(sistesalg).getTime())
  ? new Date(sistesalg).toLocaleDateString("no-NO", {
      day: "numeric",
      month: "numeric",
      year: "numeric",
    })
  : "N/A";

  return (
    <div style={{ marginLeft: 8 }}> {/* MUI spacing unit 1 = 8px */}
      {varerdata.map((item, index) => (
        <Accordion key={index} sx={{ marginBottom: 2 }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1a-content"
            id="panel1a-header"
          >
            <Typography variant="h6" component="div">
              {`${item.alpha} ${item.productNumber} ${item.name} `}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Card sx={{ backgroundColor: "#f5f5f5" }}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: "bold" }}
                      color="black"
                    >
                      Pris
                    </Typography>
                    <Typography variant="body1">{item.salesPrice}</Typography>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: "bold" }}
                      color="black"
                    >
                      Pristillegg
                    </Typography>
                    <Typography variant="body1">
                      {item.surcharge || "Ingen"}
                    </Typography>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: "bold" }}
                      color="black"
                    >
                      PristilleggType
                    </Typography>
                    <Typography variant="body1">
                      {item.surchargeType || "Ingen"}
                    </Typography>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: "bold" }}
                      color="black"
                    >
                      Beholdning
                    </Typography>
                    <Typography variant="body1">{Math.round(item.stock)}</Typography>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: "bold" }}
                      color="black"
                    >
                      Antall i restordre
                    </Typography>
                    <Typography variant="body1">{item.restCount}</Typography>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: "bold" }}
                      color="black"
                    >
                      Antall i bestilling
                    </Typography>
                    <Typography variant="body1">{item.inOrderCount}</Typography>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: "bold" }}
                      color="black"
                    >
                      Lev År/Mnd
                    </Typography>
                    <Typography variant="body1">{item.delivery}</Typography>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: "bold" }}
                      color="black"
                    >
                      Siste tilgang
                    </Typography>
                    <Typography variant="body1">
                      {new Date(item.lastAvailable).toLocaleDateString("no-NO", {
                        day: "numeric",
                        month: "numeric",
                        year: "numeric",
                      })}
                    </Typography>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: "bold" }}
                      color="black"
                    >
                      Siste salg
                    </Typography>
                    <Typography variant="body1">{sisteSalgDate}</Typography>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: "bold" }}
                      color="black"
                    >
                      Siste prisendring
                    </Typography>
                    <Typography variant="body1">
                      {new Date(item.priceChanged).toLocaleDateString("no-NO", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "2-digit",
                      })}
                    </Typography>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: "bold" }}
                      color="black"
                    >
                      Artikkel opprettet
                    </Typography>
                    <Typography variant="body1">{item.created}</Typography>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: "bold" }}
                      color="black"
                    >
                      Alternativ artikkel
                    </Typography>
                    <Typography variant="body1">
                      {item.altEdb ?? "Ingen alternativ artikkel"}
                    </Typography>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: "bold" }}
                      color="black"
                    >
                      Lokasjon
                    </Typography>
                    <Typography variant="body1">{item.location}</Typography>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: "bold" }}
                      color="black"
                    >
                      Antall i FOR2
                    </Typography>
                    <Typography variant="body1">
                      {item.for2?.quantity ?? "N/A"}
                    </Typography>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: "bold" }}
                      color="black"
                    >
                      Reservert i FOR2
                    </Typography>
                    <Typography variant="body1">
                      {item.for2?.reserved ?? "N/A"}
                    </Typography>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: "bold" }}
                      color="black"
                    >
                      Sluppet forhåndsordre
                    </Typography>
                    <Typography variant="body1">{item.sluppPreorder}</Typography>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: "bold" }}
                      color="black"
                    >
                      Restet i varesett
                    </Typography>
                    <Typography variant="body1">{item.restedInSet}</Typography>
                  </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Box display="flex" alignItems="center">
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: "bold" }}
                      color="black"
                    >
                      Merknad
                    </Typography>
                   <Tooltip
  title={
    <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 1, maxWidth: 400 }}>
      {[
        ['1->', 'UtgårKjøpeforslag blir ikke beregnet (Varen er utgått)'],
        ['2->', 'På best.Ikke beregnet når varen ikke skal føres'],
        ['M->', 'Mid-utg.Utsolgt fra leverandør'],
        ['3->', 'Nedskr.Lagerverdi er nedskrevet'],
        ['4->', 'Alt.art.Alternativ vare (brukes ikke)'],
        ['5->', 'Ikke pr.Skal ikke i prislister'],
        ['6->', 'Må best Må bestilles'],
        ['A->', 'Prisavik Skaffevarer, ikke på aviksliste'],
        ['B->', 'Ikke bonus Ikke bonus-beregning'],
        ['F->', 'Ekskludert Ikke med i faktura/bonus/gebyr'],
        ['T->', 'Tilb.pr. Vare med tilbudspris'],
        ['X->', 'Utgår Kampanjevare (samme som kode 1)'],
        ['Y->', 'Ikke lag. Ikke på lagerstyring'],
        ['Z->', 'Utgår Kommisjon (samme som kode 1)'],
        ['O->', 'ORIGINAL DEL'],
        ['', 'Bruk normalt kun kode 1 og 2.'],
      ].map(([code, desc]) => (
        <>
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{code}</Typography>
          <Typography variant="body2">{desc}</Typography>
        </>
      ))}
    </Box>
  }
  placement="top"
>
  <InfoIcon sx={{ color: "green", fontSize: 22, marginLeft: 1 }} />
</Tooltip>
                  </Box>

                  <Typography variant="body1">
  {item.remark && merknadMap[item.remark] ? (
    <>
      <strong>{item.remark}</strong>: {merknadMap[item.remark]}
    </>
  ) : (
    item.remark || 'Ingen merknad'
  )}
</Typography>

                </Grid>

                </Grid>
              </CardContent>
            </Card>
          </AccordionDetails>
        </Accordion>
      ))}
    </div>
  );
};

export default VarerDetails;
